import React, { useRef, useEffect } from "react";
import { Tooltip } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import { Tags } from "lucide-react";
import type { Tag } from "@/lib/types";

interface ChatTagSelectorProps {
  tags: Tag[];
  includeTagIds: string[];
  excludeTagIds: string[];
  onToggleTag: (tagId: string, type: "include" | "exclude") => void;
  visible: boolean;
  onToggle: () => void;
  onClose?: () => void;
}

const ChatTagSelector: React.FC<ChatTagSelectorProps> = ({
  tags,
  includeTagIds,
  excludeTagIds,
  onToggleTag,
  visible,
  onToggle,
  onClose,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const activeCount = includeTagIds.length + excludeTagIds.length;

  useEffect(() => {
    if (!visible) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        if (onClose) onClose();
        else onToggle();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        if (onClose) onClose();
        else onToggle();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [visible, onClose, onToggle]);

  return (
    <div className="chat-tag-selector" ref={containerRef}>
      <Tooltip title="Filter by Tags" placement="top">
        <button
          type="button"
          onClick={onToggle}
          className={`chat-header-btn ${visible || activeCount > 0 ? "active" : ""}`}
          aria-label="Filter by Tags"
        >
          <Tags size={15} />
          {activeCount > 0 && (
            <span className="chat-tag-badge">{activeCount}</span>
          )}
        </button>
      </Tooltip>
      {visible && (
        <div className="chat-tag-dropdown">
          <div className="chat-tag-section">
            <div className="chat-tag-section-header include">Include</div>
            <div className="chat-tag-section-list">
              {tags.map((tag) => (
                <button
                  key={`inc-${tag.id}`}
                  type="button"
                  onClick={() => onToggleTag(tag.id, "include")}
                  className={`chat-tag-option ${includeTagIds.includes(tag.id) ? "selected" : ""}`}
                >
                  <span
                    className="chat-tag-dot"
                    style={{ background: tag.color || "#6b7280" }}
                  />
                  <span className="chat-tag-name">{tag.name}</span>
                  {includeTagIds.includes(tag.id) && (
                    <CheckOutlined style={{ fontSize: 10, color: "var(--primary-color, #0069a5)" }} />
                  )}
                </button>
              ))}
              {tags.length === 0 && (
                <div className="chat-tag-empty">No tags available</div>
              )}
            </div>
          </div>
          <div className="chat-tag-section">
            <div className="chat-tag-section-header exclude">Exclude</div>
            <div className="chat-tag-section-list">
              {tags.map((tag) => (
                <button
                  key={`exc-${tag.id}`}
                  type="button"
                  onClick={() => onToggleTag(tag.id, "exclude")}
                  className={`chat-tag-option exclude ${excludeTagIds.includes(tag.id) ? "selected" : ""}`}
                >
                  <span
                    className="chat-tag-dot"
                    style={{ background: tag.color || "#6b7280" }}
                  />
                  <span className="chat-tag-name">{tag.name}</span>
                  {excludeTagIds.includes(tag.id) && (
                    <CheckOutlined style={{ fontSize: 10, color: "#dc2626" }} />
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatTagSelector;
